import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Switch,
  Typography,
} from "@material-ui/core";
import { PluginKey } from "resource-planner-model";
import { useHistory } from "react-router-dom";
import { tokens } from "../app/theme";
import {
  PLUGIN_CATALOG,
  isPluginEnabled,
  pluginSummary,
  withPluginEnabled,
} from "./pluginCatalog";
import {
  useApplicationConfig,
  usePersistApplicationConfig,
} from "./useApplicationConfig";

const VALIDATION_COLOR: Record<string, string> = {
  valid: tokens.ok,
  invalid: tokens.crit,
  unknown: tokens.fg3,
};

export function PluginsPage() {
  const history = useHistory();
  const config = useApplicationConfig();
  const persist = usePersistApplicationConfig();

  function toggle(key: PluginKey, enabled: boolean): void {
    persist(withPluginEnabled(config, key, enabled)).catch(() => {
      /* error already surfaced via app feedback */
    });
  }

  return (
    <Box>
      <Typography variant="h4">Plugins</Typography>
      <Typography variant="body2" style={{ color: tokens.fg2, marginTop: 4 }}>
        Enable and configure integrations. Secrets stay in secure storage and are
        never shown here.
      </Typography>
      <Box
        marginTop={3}
        display="grid"
        gridTemplateColumns="repeat(auto-fill, minmax(320px, 1fr))"
        gridGap={16}
      >
        {PLUGIN_CATALOG.map((entry) => {
          const enabled = isPluginEnabled(config, entry.key);
          const validation =
            config.plugins[entry.key].validation?.state;
          return (
            <Card key={entry.key} data-testid={`plugin-card-${entry.key}`}>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="h6">{entry.label}</Typography>
                  {entry.hasEnabledFlag && (
                    <Switch
                      color="primary"
                      checked={!!enabled}
                      onChange={(event) =>
                        toggle(entry.key, event.target.checked)
                      }
                      inputProps={
                        {
                          "aria-label": `${entry.label} enabled`,
                          "data-testid": `plugin-enabled-${entry.key}`,
                        } as any
                      }
                    />
                  )}
                </Box>
                <Box display="flex" alignItems="center" gridGap={8} marginTop={0.5}>
                  {entry.hasEnabledFlag && (
                    <Chip
                      size="small"
                      label={enabled ? "Enabled" : "Disabled"}
                      style={{
                        background: enabled
                          ? "hsla(142, 64%, 48%, 0.16)"
                          : tokens.hairline,
                        color: enabled ? tokens.ok : tokens.fg3,
                      }}
                    />
                  )}
                  {validation && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Validation: ${validation}`}
                      style={{ color: VALIDATION_COLOR[validation] }}
                    />
                  )}
                </Box>
                <Typography
                  variant="body2"
                  style={{ color: tokens.fg2, marginTop: 8 }}
                >
                  {entry.description}
                </Typography>
                <Box marginTop={1.5}>
                  {pluginSummary(config, entry.key).map((field) => (
                    <Box
                      key={field.label}
                      display="flex"
                      justifyContent="space-between"
                      gridGap={12}
                      paddingY={0.25}
                    >
                      <Typography
                        variant="caption"
                        style={{ color: tokens.fg3 }}
                      >
                        {field.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        style={{
                          color: tokens.fg1,
                          fontFamily: tokens.fontMono,
                          textAlign: "right",
                          wordBreak: "break-word",
                        }}
                      >
                        {field.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Box marginTop={2}>
                  <Button
                    variant="outlined"
                    size="small"
                    data-testid={`plugin-configure-${entry.key}`}
                    onClick={() =>
                      history.push(`/administration/plugins/${entry.key}`)
                    }
                  >
                    Configure
                  </Button>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
}
